import type {
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
} from "@cord/shared"

function normalizeMaybeString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function getRequestedSkillRefKey(ref: AgentRuntimeRequestedSkillRef): string {
  return ref.skillName.trim().toLowerCase()
}

function getPreferredMcpServerRefKey(ref: AgentRuntimePreferredMcpServerRef): string {
  return ref.serverKey.trim().toLowerCase()
}

export function normalizeRequestedSkillRefs(
  input: unknown,
): AgentRuntimeRequestedSkillRef[] {
  if (!Array.isArray(input)) return []

  const normalized: AgentRuntimeRequestedSkillRef[] = []
  for (const item of input) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue
    const row = item as Record<string, unknown>
    const skillName = normalizeMaybeString(row.skillName)
    if (!skillName) continue
    normalized.push({
      skillName,
      description:
        typeof row.description === "string" || row.description === null
          ? row.description
          : undefined,
      sourceType:
        row.sourceType === "managed" || row.sourceType === "repo_native"
          ? row.sourceType
          : undefined,
      scopeKind:
        row.scopeKind === "org" || row.scopeKind === "repo" || row.scopeKind === "user"
          ? row.scopeKind
          : undefined,
    })
  }

  return normalized
}

export function normalizePreferredMcpServerRefs(
  input: unknown,
): AgentRuntimePreferredMcpServerRef[] {
  if (!Array.isArray(input)) return []

  const normalized: AgentRuntimePreferredMcpServerRef[] = []
  for (const item of input) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue
    const row = item as Record<string, unknown>
    const serverKey = normalizeMaybeString(row.serverKey)
    if (!serverKey) continue
    normalized.push({
      serverKey,
      displayName:
        typeof row.displayName === "string" || row.displayName === null
          ? row.displayName
          : undefined,
      description:
        typeof row.description === "string" || row.description === null
          ? row.description
          : undefined,
      upstreamUrl:
        typeof row.upstreamUrl === "string" || row.upstreamUrl === null
          ? row.upstreamUrl
          : undefined,
    })
  }

  return normalized
}

export function dedupeRequestedSkillRefs(
  refs: AgentRuntimeRequestedSkillRef[],
): AgentRuntimeRequestedSkillRef[] {
  const byKey = new Map<string, AgentRuntimeRequestedSkillRef>()
  for (const ref of refs) {
    const key = getRequestedSkillRefKey(ref)
    if (!key) continue
    byKey.set(key, ref)
  }
  return Array.from(byKey.values())
}

export function dedupePreferredMcpServerRefs(
  refs: AgentRuntimePreferredMcpServerRef[],
): AgentRuntimePreferredMcpServerRef[] {
  const byKey = new Map<string, AgentRuntimePreferredMcpServerRef>()
  for (const ref of refs) {
    const key = getPreferredMcpServerRefKey(ref)
    if (!key) continue
    byKey.set(key, ref)
  }
  return Array.from(byKey.values())
}

export function sameRequestedSkillRefs(
  left: AgentRuntimeRequestedSkillRef[],
  right: AgentRuntimeRequestedSkillRef[],
): boolean {
  if (left === right) return true
  if (left.length !== right.length) return false

  for (let index = 0; index < left.length; index += 1) {
    const leftRef = left[index]
    const rightRef = right[index]
    if (
      leftRef.skillName !== rightRef.skillName
      || leftRef.description !== rightRef.description
      || leftRef.sourceType !== rightRef.sourceType
      || leftRef.scopeKind !== rightRef.scopeKind
    ) {
      return false
    }
  }

  return true
}

export function samePreferredMcpServerRefs(
  left: AgentRuntimePreferredMcpServerRef[],
  right: AgentRuntimePreferredMcpServerRef[],
): boolean {
  if (left === right) return true
  if (left.length !== right.length) return false

  for (let index = 0; index < left.length; index += 1) {
    const leftRef = left[index]
    const rightRef = right[index]
    if (
      leftRef.serverKey !== rightRef.serverKey
      || leftRef.displayName !== rightRef.displayName
      || leftRef.description !== rightRef.description
      || leftRef.upstreamUrl !== rightRef.upstreamUrl
    ) {
      return false
    }
  }

  return true
}

export function getRequestedSkillRefDisplayName(
  ref: AgentRuntimeRequestedSkillRef,
): string {
  return ref.skillName.trim()
}

export function getRequestedSkillRefSourceLabel(
  ref: AgentRuntimeRequestedSkillRef,
): string {
  if (ref.sourceType === "repo_native" || ref.scopeKind === "repo") {
    return "Repo skill"
  }
  if (ref.scopeKind === "org") {
    return "Org skill"
  }
  if (ref.scopeKind === "user") {
    return "User skill"
  }
  return "Managed skill"
}

export function getRequestedSkillRefSourceBadge(
  ref: AgentRuntimeRequestedSkillRef,
): string {
  if (ref.sourceType === "repo_native" || ref.scopeKind === "repo") {
    return "repo"
  }
  if (ref.scopeKind === "org") {
    return "org"
  }
  if (ref.scopeKind === "user") {
    return "user"
  }
  return "managed"
}

export function getPreferredMcpServerRefDisplayName(
  ref: AgentRuntimePreferredMcpServerRef,
): string {
  const displayName = typeof ref.displayName === "string" ? ref.displayName.trim() : ""
  return displayName.length > 0 ? displayName : ref.serverKey.trim()
}

export function getPreferredMcpServerRefSecondaryLabel(
  ref: AgentRuntimePreferredMcpServerRef,
): string | null {
  const upstreamUrl = typeof ref.upstreamUrl === "string" ? ref.upstreamUrl.trim() : ""
  if (!upstreamUrl) return null

  try {
    const parsed = new URL(upstreamUrl)
    return parsed.hostname || null
  } catch {
    return upstreamUrl
  }
}
